import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import StaffNavbar from '../components/StaffNavbar';
import apiService from '../services/api';
import '../styles/StaffPages.css';

// The dashboard is a link index, so it is easier to reorder as data than as
// markup. Order here is the order on screen.
const SECTIONS = [
  {
    title: 'Management',
    blurb: 'Core platform administration and data management',
    links: [
      { label: 'Manage Schools', path: '/admin/school-management' },
      { label: 'Manage Transactions', path: '/superuser/transactions/' },
      { label: 'Manage Users', path: '/superuser/users/' },
      { label: 'Analytics', path: '/superuser/analytics/' },
      { label: 'AI Insights', path: '/superuser/insights/' },
      { label: 'Data Portal', path: '/superuser/data-portal/' },
    ],
  },
  {
    title: 'School Partner Links',
    blurb: 'Partner-facing tools and onboarding',
    links: [
      { label: 'Staff User Links', path: '/superuser/link-generator/' },
      { label: 'Onboarding', path: '/superuser/onboarding/' },
    ],
  },
  {
    title: 'Financials',
    blurb: 'Money in from merchants, money out to schools, and the trail behind both',
    links: [
      { label: 'Merchant Pay', path: '/superuser/invoices/' },
      { label: 'School Pay', path: '/superuser/school-pay/' },
      { label: 'Audit', path: '/superuser/audit/' },
    ],
  },
  {
    title: 'Merchants',
    blurb: 'Merchant directory and reporting',
    links: [
      { label: 'Olive Merchants', path: '/superuser/olive-merchants/' },
      { label: 'Wildfire Merchants', path: '/superuser/wildfire-merchants/' },
    ],
  },
  {
    title: 'System',
    blurb: 'Points, incentives and account setup',
    links: [
      { label: 'Review Receipts', path: '/superuser/receipts/' },
      { label: 'Account Setup Points', path: '/superuser/setup-task-rewards/' },
      { label: 'Account Setup Progress', path: '/superuser/users-progress/' },
      { label: 'Point Multipliers', path: '/superuser/point-multipliers/' },
      { label: 'Manage Incentives', path: '/superuser/incentives/' },
      { label: 'Send Notifications', path: '/superuser/notifications/' },
      { label: 'Card Draw Management', path: '/superuser/card-draw/' },
      { label: 'Hidden from Rewards', path: '/superuser/reward-exclusions/' },
      { label: 'Check-In Locations', path: '/superuser/check-in/' },
      { label: 'Challenges & Badges', path: '/superuser/challenge-presets/' },
      { label: 'Challenge schedule', path: '/superuser/school-challenges/' },
      { label: 'Send Test Free Entries', path: '/superuser/test-free-entries/' },
    ],
  },
  {
    title: 'Engineering Links',
    blurb: 'Developer documentation and technical resources',
    links: [
      { label: 'Access Pulls', path: '/superuser/access-pulls/' },
      { label: 'Docs', path: '/superuser/docs/' },
    ],
  },
];

const SuperuserDashboard = () => {
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const currentUser = await apiService.fetchCurrentUser();

        // Redirect if user is not superuser
        if (!currentUser?.is_superuser) {
          navigate('/staff/dashboard/');
          return;
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
        navigate('/login/');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [navigate]);

  if (loading) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection="superuser" />
        <div className="admin-main">
          <div className="admin-content">
            <div style={{
              textAlign: 'center',
              padding: '3rem',
              color: '#666'
            }}>
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="superuser" />
      <div className="admin-main">
        <div className="admin-content">
          <h1 className="staff-page-header">Superuser Dashboard</h1>
          <p className="staff-page-subheader">Platform administration and management</p>

          {SECTIONS.map(section => (
            <div key={section.title} style={{ marginTop: '48px' }}>
              <h2 className="staff-section-header">{section.title}</h2>
              <p className="staff-page-content">{section.blurb}</p>
              <div className="dashboard-actions">
                {section.links.map(link => (
                  <button
                    key={link.path}
                    className="dashboard-button"
                    onClick={() => navigate(link.path)}
                  >
                    {link.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SuperuserDashboard;